import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Building2, KeyRound, LayoutDashboard, Plus } from 'lucide-react'
import {
  Avatar,
  AvatarFallback,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
} from '@platform/ui'
import { resumenEmisor, useEstadoEmisor } from '@/lib/demo/emisor'
import { registrar } from '@/lib/demo/telemetry'
import { prospectoActual } from '@/lib/demo/lead-ref'
import { platform } from '@/lib/platform'
import { paso } from '@/modules/demo/DemoTour'
import { BrandLogo } from '@/modules/shared/BrandLogo'
import { ClaveAcceso } from '@/modules/emisor/ClaveAcceso'
import { DetalleActivo } from '@/modules/emisor/DetalleActivo'
import { ListaActivos } from '@/modules/emisor/ListaActivos'
import { PublicarActivo } from '@/modules/emisor/PublicarActivo'
import { Resumen } from '@/modules/emisor/Resumen'
import { dinero } from '@/modules/emisor/formato'

/**
 * El panel del emisor: la plataforma vista desde el lado del que la opera.
 *
 * POR QUÉ LA NAVEGACIÓN ES POR ESTADO Y NO POR RUTAS
 *
 * Lo natural sería una ruta por pantalla —`/dashboard/admin/activos/:id`— y sería
 * un error aquí. El panel no persiste nada: los activos que alguien publica viven
 * en memoria y se pierden al recargar. Una dirección compartible o un marcador
 * prometen que volver a esa dirección devuelve lo mismo, y con este estado la
 * promesa se rompe justo con los activos recién creados, que son los que a
 * alguien se le ocurriría querer volver a ver.
 *
 * Navegando por estado, el ámbito de lo que se puede visitar y el ámbito de lo
 * que existe son el mismo. Recargar devuelve al principio, que es exactamente lo
 * que hace el estado por debajo.
 *
 * SOBRE LOS TEXTOS: hablan del producto y nunca del entorno. Quien mira esto está
 * evaluando si compra la plataforma; cada aclaración de que lo que ve no cuenta
 * es una razón para dejar de mirarla en serio.
 */

type Vista =
  | { pantalla: 'resumen' }
  | { pantalla: 'activos' }
  | { pantalla: 'detalle'; activoId: string }
  | { pantalla: 'publicar' }
  | { pantalla: 'acceso' }

const TITULOS: Record<Vista['pantalla'], { titulo: string; detalle: string }> = {
  resumen: { titulo: 'Panel del emisor', detalle: 'Tu cartera de activos, de un vistazo' },
  activos: { titulo: 'Activos', detalle: 'Todo lo que tenés publicado' },
  detalle: { titulo: 'Activos', detalle: 'Ficha del activo y su operación' },
  publicar: { titulo: 'Publicar activo', detalle: 'Cinco pasos y queda en el catálogo' },
  acceso: { titulo: 'Acceso', detalle: 'Conectá la plataforma con tus sistemas' },
}

function PanelEmisor() {
  const estado = useEstadoEmisor()
  const [vista, setVista] = useState<Vista>({ pantalla: 'resumen' })
  const r = resumenEmisor(estado)
  const marca = platform.brand?.productName ?? 'la plataforma'
  const nombre = prospectoActual()?.name?.trim() || marca

  /*
   * Llegar al panel del emisor es la señal del cruce: dejó de evaluar la
   * inversión y empezó a evaluar el producto.
   *
   * El conmutador flotante ya la emite antes de navegar, y aun así se emite otra
   * vez acá. No es una duplicación: se puede entrar sin pasar por el conmutador
   * —recargando, o desde el recorrido guiado— y esa entrada cuenta igual. La
   * deduplicación por prospecto y día se encarga de que no sume dos veces.
   */
  useEffect(() => {
    registrar('issuer_panel_opened', { via: 'panel' })
  }, [])

  const cabecera = TITULOS[vista.pantalla]

  const irA = (pantalla: Exclude<Vista['pantalla'], 'detalle'>) => setVista({ pantalla })
  const abrirActivo = (activoId: string) => setVista({ pantalla: 'detalle', activoId })

  const enSeccion = (pantalla: Vista['pantalla']) =>
    vista.pantalla === pantalla || (pantalla === 'activos' && vista.pantalla === 'detalle')

  return (
    <SidebarProvider style={{ '--sidebar-width-icon': '2.5rem' } as React.CSSProperties}>
      <Sidebar variant="floating" collapsible="icon" className="p-3 group-data-[collapsible=icon]:!p-1 platform-sidebar">
        <SidebarHeader className="px-3 py-4 group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:py-2">
          <div className="flex items-center justify-between group-data-[collapsible=icon]:justify-center">
            <div className="group-data-[collapsible=icon]:hidden">
              <BrandLogo variant="primary" textClassName="text-lg font-semibold tracking-tight" />
            </div>
            <div className="hidden h-7 w-7 items-center justify-center rounded-md bg-primary/15 text-xs font-bold text-primary group-data-[collapsible=icon]:flex">
              {marca.charAt(0).toUpperCase()}
            </div>
          </div>
        </SidebarHeader>
        <SidebarSeparator className="mx-3" />

        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupLabel className="text-xs uppercase tracking-wider text-muted-foreground/60">
              Emisión
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu className="gap-1">
                <SidebarMenuItem>
                  <SidebarMenuButton
                    tooltip="Resumen"
                    isActive={enSeccion('resumen')}
                    onClick={() => irA('resumen')}
                    className="h-10"
                  >
                    <LayoutDashboard className="h-4 w-4" />
                    <span>Resumen</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>

                <SidebarMenuItem>
                  <SidebarMenuButton
                    tooltip="Activos"
                    isActive={enSeccion('activos')}
                    onClick={() => irA('activos')}
                    className="h-10"
                  >
                    <Building2 className="h-4 w-4" />
                    <span>Activos</span>
                  </SidebarMenuButton>
                  <SidebarMenuBadge className="bg-primary/20 text-primary">
                    {r.activos}
                  </SidebarMenuBadge>
                </SidebarMenuItem>

                <SidebarMenuItem>
                  <SidebarMenuButton
                    tooltip="Publicar activo"
                    isActive={enSeccion('publicar')}
                    onClick={() => irA('publicar')}
                    className="h-10"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Publicar activo</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          <SidebarGroup>
            <SidebarGroupLabel className="text-xs uppercase tracking-wider text-muted-foreground/60">
              Plataforma
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu className="gap-1">
                <SidebarMenuItem>
                  <SidebarMenuButton
                    tooltip="Clave de acceso"
                    isActive={enSeccion('acceso')}
                    onClick={() => irA('acceso')}
                    className="h-10"
                  >
                    <KeyRound className="h-4 w-4" />
                    <span>Clave de acceso</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        <SidebarRail />

        <SidebarFooter className="px-3 py-3 group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:py-2">
          <div className="mb-1 flex items-center gap-3 rounded-xl border border-primary/10 bg-gradient-to-r from-primary/10 to-primary/5 p-3 group-data-[collapsible=icon]:hidden">
            <Avatar className="h-10 w-10 border-2 border-primary/30 ring-2 ring-primary/10">
              <AvatarFallback className="bg-primary/20 font-bold text-primary">
                {nombre.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold">{nombre}</p>
              <p className="truncate text-xs text-muted-foreground">Emisor</p>
            </div>
          </div>
          <div className="hidden justify-center group-data-[collapsible=icon]:flex">
            <Avatar className="h-7 w-7 border border-primary/30">
              <AvatarFallback className="bg-primary/20 text-[10px] font-bold text-primary">
                {nombre.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
          </div>
        </SidebarFooter>
      </Sidebar>

      <SidebarInset className="relative min-w-0 bg-background">
        <div aria-hidden className="pointer-events-none fixed inset-0 bg-mesh-soft opacity-60" />

        <header className="bg-transparent px-4 py-6 sm:px-8" {...paso('panel-emisor')}>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex min-w-0 items-center gap-3">
              <SidebarTrigger className="h-9 w-9 rounded-lg transition-colors hover:bg-muted" />
              <div className="min-w-0">
                <h1 className="truncate text-2xl font-bold text-foreground">{cabecera.titulo}</h1>
                <p className="truncate text-sm text-muted-foreground">{cabecera.detalle}</p>
              </div>
            </div>

            {/*
              En el resumen el recaudado ya es el primer indicador de la fila.
              Repetirlo en la cabecera no informa: gasta atención en un número que
              está tres centímetros más abajo y más grande.
            */}
            {vista.pantalla === 'resumen' ? null : (
              <div className="flex items-center gap-2 rounded-full border border-border/60 bg-card/70 px-4 py-2 backdrop-blur">
                <span className="text-xs uppercase tracking-wider text-muted-foreground">
                  Recaudado
                </span>
                <span className="text-sm font-bold tabular-nums">{dinero(r.recaudado)}</span>
              </div>
            )}
          </div>
        </header>

        {/*
          El hueco de abajo no es estético: el conmutador flotante y la llamada a
          la acción viven fijos en esa esquina, y sin margen se comen la última
          tarjeta de cualquier columna.
        */}
        <main className="px-4 pb-40 sm:px-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={vista.pantalla === 'detalle' ? `detalle-${vista.activoId}` : vista.pantalla}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.25 }}
            >
              {vista.pantalla === 'resumen' ? <Resumen alAbrirActivo={abrirActivo} /> : null}

              {vista.pantalla === 'activos' ? (
                <ListaActivos alAbrirActivo={abrirActivo} alPublicar={() => irA('publicar')} />
              ) : null}

              {vista.pantalla === 'detalle' ? (
                <DetalleActivo activoId={vista.activoId} alVolver={() => irA('activos')} />
              ) : null}

              {vista.pantalla === 'publicar' ? (
                <PublicarActivo alPublicado={abrirActivo} alCancelar={() => irA('activos')} />
              ) : null}

              {vista.pantalla === 'acceso' ? <ClaveAcceso /> : null}
            </motion.div>
          </AnimatePresence>
        </main>
      </SidebarInset>
    </SidebarProvider>
  )
}

export const Route = createFileRoute('/dashboard/admin/')({
  component: PanelEmisor,
})
